import * as React from 'react';
import { Section, Head, Body, Img, Link } from '@react-email/components';
import { Text } from '@react-email/text';
import { Html } from '@react-email/components';
import { Header, Preheader, Events, Footer_v2 } from '../components';
import { KYDEventSummary } from '../../interfaces/KYDEvent';
import { KYDBlockData } from '../../interfaces/KYDCampaign';
import { KYDSocials } from '../../interfaces/KYDOrganization';
import EventList from '../components/EventList';
import { createTrackableLink } from '../utils/link';

const PersonalUserDataTemplateReplacement = {
  unsubscribe_url: '{{unsubscribe_url}}',
};

function NewCampaign({
  blocks,
  force_desktop = false,
  preheader,
  subject,
  organization_logo_url,
  organization_website,
  socials,
  campaign_id,
  address,
}: {
  blocks: KYDBlockData[];
  subject: string;
  force_desktop?: boolean;
  related_shows?: KYDEventSummary[];
  preheader?: string;
  organization_logo_url?: string;
  organization_website?: string;
  socials?: KYDSocials;
  campaign_id?: string;
  address: string;
}) {
  const logo_url = 'https://content.kydlabs.com/system/kyd-email-logo.png';

  const renderBlock = (block: KYDBlockData): React.ReactNode => {
    switch (block.type) {
      case 'textHeadline': {
        const { id, preheadline, headline, subheadline } = block;
        return (
          <Section key={id} style={{ margin: '0 auto', padding: '24px' }}>
            <Text style={preheadlineStyle}>{preheadline}</Text>
            {block.link_headline_to_event ? (
              <Link
                style={{ textDecoration: 'none', color: 'black' }}
                href={
                  block.event?.link
                    ? createTrackableLink(block.event.link, {
                        utm_campaign: campaign_id,
                        utm_source: 'kyd',
                        utm_medium: 'email',
                        utm_id: campaign_id,
                      })
                    : 'https://www.kydlabs.com'
                }
              >
                <Text style={headlineStyle}>{headline}</Text>
                <Text></Text>
              </Link>
            ) : (
              <Text style={headlineStyle}>{headline}</Text>
            )}
            <Text style={subheadlineStyle}>{subheadline}</Text>
          </Section>
        );
      }
      case 'textBody': {
        const { id, body } = block;
        return (
          <Section key={id} style={{ padding: '24px' }}>
            {body.split('\n').map((line, index) => (
              <Text key={index} style={bodyStyle}>
                {line || '\n'}
              </Text>
            ))}
          </Section>
        );
      }
      case 'event1col': {
        const { id, event } = block;
        return (
          <Section key={id} style={{ padding: '24px' }}>
            {/* Render single event summary */}
            <Events events={[event]} campaign_id={campaign_id} />
          </Section>
        );
      }
      case 'event2col': {
        const { id, events } = block;
        return (
          <Section key={id} style={{ padding: '24px' }}>
            <Events events={events} campaign_id={campaign_id} />
          </Section>
        );
      }
      case 'eventList': {
        const { id, events, list_heading } = block;
        return (
          <Section key={id} style={{ padding: '0px' }}>
            <Section style={{ padding: '24px', backgroundColor: 'black' }}>
              <EventList
                list_heading={list_heading}
                events={events}
                campaign_id={campaign_id}
              />
            </Section>
          </Section>
        );
      }
      case 'image': {
        const { id, src, link } = block;
        return (
          <Section key={id} style={{ padding: '0px' }}>
            <Section style={{ padding: '24px', backgroundColor: 'black' }}>
              <Link
                href={
                  link
                    ? createTrackableLink(link, {
                        utm_campaign: campaign_id,
                        utm_source: 'kyd',
                        utm_medium: 'email',
                        utm_id: campaign_id,
                      })
                    : 'https://www.kydlabs.com'
                }
                style={{ textDecoration: 'none', color: 'black' }}
              >
                <Img src={src} style={{ width: '100%', height: 'auto' }} />
              </Link>
            </Section>
          </Section>
        );
      }
      case 'video': {
        const { id, src, preview_src } = block;
        const videoId = src.split('v=')[1]?.split('&')[0] || '';
        const thumbnailUrl =
          preview_src || `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

        return (
          <Section key={id} style={{ padding: '0px' }}>
            <Section style={{ padding: '24px' }}>
              <Link
                href={src}
                style={{
                  display: 'inline-block',
                  textDecoration: 'none',
                  position: 'relative',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                <Img
                  src={thumbnailUrl}
                  alt="Play"
                  style={{
                    width: '100%',
                    maxWidth: '600px',
                    height: 'auto',
                    display: 'block',
                    margin: '0 auto',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                  }}
                >
                  <Img
                    src="https://cdn-icons-png.flaticon.com/512/727/727245.png"
                    alt="Play"
                    width="64"
                    height="64"
                    style={{
                      display: 'inline-block',
                    }}
                  />
                </div>
              </Link>
            </Section>
          </Section>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Html>
      <Head>
        <style>
          {`
            body {
              margin: 0 !important;
              padding: 0 !important;
            }
          `}
        </style>
        <title>{subject}</title>
      </Head>
      <Body
        style={{
          width: '100%',
          maxWidth: '600px',
          margin: '0px auto !important',
          padding: '0 0',
        }}
      >
        {preheader && <Preheader preheader={preheader} />}
        {/* <Header src={logo_url} style={{ margin: '0px auto' }} /> */}
        {organization_logo_url && (
          <Img
            src={organization_logo_url}
            style={{ margin: '24px auto', height: '110px' }}
          />
        )}
        {blocks?.map(renderBlock)}
        <Footer_v2
          src={logo_url}
          address={address}
          unsubscribe_url={PersonalUserDataTemplateReplacement.unsubscribe_url}
          organization_website={organization_website}
          socials={socials}
        />
      </Body>
    </Html>
  );
}

const preheadlineStyle: React.CSSProperties = {
  fontSize: '12px',
  lineHeight: '140%',
  letterSpacing: '-3%',
  marginBottom: '10px',
  marginTop: '10px',
  color: 'black',
  fontWeight: '400',
  fontFamily: 'Andale Mono',
  textAlign: 'center',
};

const headlineStyle: React.CSSProperties = {
  fontSize: '26px',
  fontWeight: '400',
  lineHeight: '130%',
  letterSpacing: '-3%',
  marginBottom: '10px',
  textAlign: 'center',
  fontFamily: 'Helvetica',
};

const subheadlineStyle: React.CSSProperties = {
  fontSize: '14px',
  marginBottom: '24px',
  marginTop: '10px',
  fontWeight: 'normal',
  textAlign: 'center',
  lineHeight: '140%',
  fontFamily: 'Helvetica',
  color: '#000000B2',
};

const bodyStyle: React.CSSProperties = {
  fontSize: '16px',
  lineHeight: '24px',
  fontFamily: 'Helvetica',
  color: '#333',
};

export const TemplateVersion = {
  1: NewCampaign,
  2: NewCampaign,
};
